import { useMemo } from "react";
import { dateToX, parseDate } from "@/lib/gantt-utils";

function firstDate(task, dateCols) {
  const dates = task.dates || {};
  let min = null;
  for (const col of dateCols) {
    const v = dates[col.id];
    if (!v) continue;
    const d = parseDate(v);
    if (!d) continue;
    if (min == null || d < min) min = d;
  }
  return min;
}

function lastDate(task, dateCols) {
  const dates = task.dates || {};
  let max = null;
  for (const col of dateCols) {
    const v = dates[col.id];
    if (!v) continue;
    const d = parseDate(v);
    if (!d) continue;
    if (max == null || d > max) max = d;
  }
  return max;
}

/**
 * Computes the array of SVG arrow path inputs from both legacy free-text
 * `task.dependencies` and structured `task.dependency_links`. Returns
 * `[{sx, sy, tx, ty, srcIdx, dstIdx}]`.
 *
 * An empty list if there's no drawable dependency (e.g. both endpoints have no
 * dates). Arrows are also skipped when src == dst to avoid self-loops.
 */
export default function useDependencyArrows({ tasks, dateCols, range, pxPerDay, rowHeight }) {
  return useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    const idxById = new Map(tasks.map((t, i) => [t.id, i]));
    const arrows = [];

    const pushEdge = (srcIdx, dstIdx) => {
      if (srcIdx < 0 || dstIdx < 0 || srcIdx === dstIdx) return;
      const src = tasks[srcIdx];
      const dst = tasks[dstIdx];
      const srcEnd = lastDate(src, dateCols);
      const dstStart = firstDate(dst, dateCols);
      if (srcEnd == null || dstStart == null) return;
      const sx = dateToX(srcEnd, range.start, pxPerDay);
      const sy = srcIdx * rowHeight + rowHeight / 2;
      const tx = dateToX(dstStart, range.start, pxPerDay);
      const ty = dstIdx * rowHeight + rowHeight / 2;
      arrows.push({ sx, sy, tx, ty, srcIdx, dstIdx });
    };

    tasks.forEach((t, i) => {
      // Legacy free-text deps (numbers → row position; strings → task id)
      const deps = (t.dependencies || "")
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      deps.forEach((d) => {
        let srcIdx = -1;
        const asNum = parseInt(d, 10);
        if (String(asNum) === d && asNum >= 1 && asNum <= tasks.length) {
          srcIdx = asNum - 1;
        } else if (idxById.has(d)) {
          srcIdx = idxById.get(d);
        }
        if (srcIdx >= 0) pushEdge(srcIdx, i);
      });

      // Structured links
      (t.dependency_links || []).forEach((l) => {
        const otherIdx = idxById.has(l.target_id) ? idxById.get(l.target_id) : -1;
        if (otherIdx < 0) return;
        if (l.kind === "blocks") pushEdge(i, otherIdx);
        else pushEdge(otherIdx, i);
      });
    });
    return arrows;
  }, [tasks, dateCols, range, pxPerDay, rowHeight]);
}
